import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import useIsMobile from '../../hooks/useIsMobile';
import CompactDialog from './CompactDialog';
import { ICON_STACK_CLASS_NAME, WARNING_ICON_STACK_CLASS_NAME } from '../../theme/constants/stack';
import WarningIcon from '../../assets/icons/banco-de-chile/WarningIcon';

const CreditLineErrorDialog = ({
  open,
  onClose,
  config,
  debtorNames = [],
  onEvaluateLine,
  evaluateLineLoading,
  onEvaluateInvoices,
  evaluateInvoicesLoading,
}) => {
  const isMobile = useIsMobile();
  const { branding } = useTheme();
  const theme = useTheme();

  if (!config) return null;

  const { useWarningIcon, showSuggestion, useEvaluateInvoices, showDebtors, buttonLabels } = config;

  const handlePrimaryButtonClick = useEvaluateInvoices ? onEvaluateInvoices : onEvaluateLine;
  const primaryButtonLoading = useEvaluateInvoices ? evaluateInvoicesLoading : evaluateLineLoading;

  const titleColor = useWarningIcon ? theme.palette.warning?.A600 || 'warning.main' : 'text.emphasis';
  const iconToUse = useWarningIcon ? WarningIcon : branding.icons.information;
  const iconClassName = useWarningIcon ? WARNING_ICON_STACK_CLASS_NAME : ICON_STACK_CLASS_NAME;

  return (
    <CompactDialog
      open={open}
      onClose={onClose}
      maxWidth={595}
      title={<Typography variant="large_1" sx={{ color: titleColor }}>{config.title}</Typography>}
      topIcon={iconToUse}
      topIconProps={{
        svgWrapperClassName: iconClassName,
        sx: { width: 40 },
      }}
      titleBackgroundColor="transparent"
      hideCloseButton
      spacing={3}
    >
      <Typography
        variant={isMobile ? 'small_2' : 'small_3'}
        textAlign="center"
        fontWeight={400}
        sx={{ color: 'text.secondary' }}
      >
        {config.description}
      </Typography>
      {showDebtors && debtorNames.length > 0 && (
        <Box
          sx={{
            width: '100%',
            maxHeight: isMobile ? 200 : 300,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <Stack spacing={1} alignItems="center" width="100%">
            {debtorNames.map((debtor) => (
              <Typography
                key={debtor.rut || debtor.name}
                variant={isMobile ? 'small_2' : 'small_3'}
                textAlign="center"
                fontWeight={600}
                sx={{ color: 'text.secondary' }}
              >
                {debtor.name}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
      {showSuggestion && (
        <Typography
          variant="small_3"
          textAlign="center"
          sx={{ color: 'text.secondary' }}
        >
          Sugerimos{' '}
          <Box
            component="span"
            sx={{ color: 'text.highlight' }}
          >
            eliminar algunas facturas
          </Box>
          {' '}para continuar con la evaluación o iniciar un{' '}
          <Box
            component="span"
            sx={{ color: 'text.highlight' }}
          >
            proceso de aumento de línea
          </Box>
          {' '}para revisar todas las facturas seleccionadas.
        </Typography>
      )}
      <Stack alignItems="center" width="100%" direction="row" spacing={1} justifyContent="center">
        <Button variant="secondary" onClick={onClose} id="credit-line-error-dialog-remove-invoices" sx={{ height: 40 }} disabled={primaryButtonLoading}><Typography variant="small_2">{buttonLabels.secondary}</Typography></Button>
        <LoadingButton
          variant="contained"
          onClick={handlePrimaryButtonClick}
          id="credit-line-error-dialog-evaluate-line"
          loading={primaryButtonLoading}
          disabled={!handlePrimaryButtonClick}
          sx={{ height: 40 }}
        >
          <Typography variant="small_2" fontWeight={700}>{buttonLabels.primary}</Typography>
        </LoadingButton>
      </Stack>
    </CompactDialog>
  );
};

CreditLineErrorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    useWarningIcon: PropTypes.bool.isRequired,
    showSuggestion: PropTypes.bool.isRequired,
    useEvaluateInvoices: PropTypes.bool.isRequired,
    showDebtors: PropTypes.bool.isRequired,
    buttonLabels: PropTypes.shape({
      primary: PropTypes.string.isRequired,
      secondary: PropTypes.string.isRequired,
    }).isRequired,
  }),
  debtorNames: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      rut: PropTypes.string,
    }),
  ),
  onEvaluateLine: PropTypes.func,
  evaluateLineLoading: PropTypes.bool,
  onEvaluateInvoices: PropTypes.func,
  evaluateInvoicesLoading: PropTypes.bool,
};

CreditLineErrorDialog.defaultProps = {
  config: null,
  debtorNames: [],
  onEvaluateLine: null,
  evaluateLineLoading: false,
  onEvaluateInvoices: null,
  evaluateInvoicesLoading: false,
};

export default CreditLineErrorDialog;
